import { Dialog } from '@components/dialog';
import { useIndication } from '@services/indications/useIndication';
import { Grid } from '@mui/material';
import { useEffect, useCallback, useMemo } from 'react';
import { isValid, addDays } from 'date-fns';
import { useIndicationConversion } from '@services/indications/useIndicationConversion';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useProductOfferings } from '@services/productOffering/useProductOfferings';
import { fakeLocalMidnightFromTZMidnight } from '@utils/date';
import { getApiErrorMessage } from '@utils/getApiErrorMessage';
import { Loading } from '@components/Loading';
import { QuoteConversionPostResponse } from '@lio/api-types';
import { FormFieldType, TFormField } from '@models/forms/formFields.type';
import { Form, FormSection } from '@components/forms';
import { useForm } from 'react-hook-form';
import { ConvertToQuoteFormValues } from '@pages/IndicationDetails/ConvertToQuoteFormValues.type';
import { formatFormValues } from '@components/forms/formatFormValues';

interface ConvertToQuoteDialogProps {
  isOpen: boolean;
  handleClose: (b: boolean) => void;
  indicationId: string;
}

export const ConvertToQuoteDialog = ({ indicationId, isOpen, handleClose }: ConvertToQuoteDialogProps) => {
  const { data: indication, isLoading } = useIndication(indicationId);
  const suggestedInsuranceProducts = indication?.suggestedInsuranceProducts;
  const { convertToQuote, isPending } = useIndicationConversion(indicationId);

  const navigate = useNavigate();
  const methods = useForm<ConvertToQuoteFormValues>({
    mode: 'onChange',
  });

  // can get product id this way
  const formValues = methods.getValues();
  const productId = methods.watch('productId');

  const businessClasses = useMemo(
    () => indication?.productOptions.filter((option) => option.offeringIds.includes(productId)),
    [indication, productId],
  );

  const primaryLocationJurisdictionId = indication?.indicationLocations.find((location) => location.isPrimary)
    ?.insuredLocation.address.jurisdiction.id;

  const { productOfferings } = useProductOfferings({
    brokerageId: indication?.brokerage?.id,
    jurisdictionId: primaryLocationJurisdictionId,
  });

  const filteredProductOfferings = useMemo(
    () =>
      productOfferings?.items
        .map((offering) => {
          const matchingSuggestedProduct = suggestedInsuranceProducts?.find(
            (product) => product.id === offering.insuranceProduct.id,
          );

          if (!matchingSuggestedProduct) return null;

          return {
            name: matchingSuggestedProduct.name,
            id: offering.id,
            insuranceProduct: Object.assign(matchingSuggestedProduct, offering.insuranceProduct),
            jurisdiction: offering.brokerageOfferingAvailability.jurisdictions[0],
          };
        })
        .filter(Boolean),
    [productOfferings, suggestedInsuranceProducts],
  );

  useEffect(() => {
    if (filteredProductOfferings?.length === 1) {
      const prev = methods.getValues();
      methods.reset({
        ...prev,
        productId: filteredProductOfferings[0]?.id || '',
        businessClass: (businessClasses && businessClasses[0]?.id) || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredProductOfferings, businessClasses]);

  const getEffectiveDateRange = useCallback(() => {
    const selectedOffering = filteredProductOfferings?.find((offering) => offering?.id === productId);
    const effectiveThrough = selectedOffering?.jurisdiction?.effectiveThrough
      ? new Date(selectedOffering?.jurisdiction?.effectiveThrough)
      : addDays(new Date(), 120);
    return {
      minDate: selectedOffering?.jurisdiction?.effectiveOn
        ? new Date(selectedOffering.jurisdiction?.effectiveOn)
        : new Date(),
      maxDate: new Date(Math.min(addDays(new Date(), 120).valueOf(), effectiveThrough.valueOf())),
    };
  }, [filteredProductOfferings, productId]);

  const minDate = isValid(getEffectiveDateRange().minDate)
    ? fakeLocalMidnightFromTZMidnight(getEffectiveDateRange().minDate)
    : fakeLocalMidnightFromTZMidnight(formValues.policyEffectiveDate);
  const maxDate = isValid(getEffectiveDateRange().maxDate)
    ? fakeLocalMidnightFromTZMidnight(getEffectiveDateRange().maxDate)
    : null;

  useEffect(() => {
    if (indication) {
      let effectiveOn = indication?.effectiveOn < minDate ? minDate : indication?.effectiveOn;

      methods.reset({
        productId: indication?.suggestedInsuranceProducts[0]?.id,
        policyEffectiveDate: effectiveOn,
        policyExpirationDate: indication?.expiresOn,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indication]);

  const handleIndicationConversion = () => {
    const values = methods.getValues();
    const formattedValues = formatFormValues<ConvertToQuoteFormValues>(values, fields);

    convertToQuote(
      {
        offeringId: formattedValues.productId,
        effectiveOn: formattedValues.policyEffectiveDate,
        expiresOn: formattedValues.policyExpirationDate,
        productOptionId: formattedValues.businessClass,
      },
      {
        onError: async (e) => {
          const message = await getApiErrorMessage(
            e,
            'Failed to create quote. Please contact support at 1-877-LIO-7155 (1-877-546-7155) for assistance.',
          );
          enqueueSnackbar(message, {
            variant: 'error',
          });
        },
        onSuccess: (data: QuoteConversionPostResponse) => {
          enqueueSnackbar('Quote created successfully', {
            variant: 'success',
          });
          handleClose(true);
          navigate(`/quote/${data.quote.id}`);
        },
      },
    );
  };

  const isFormValid = methods.formState.isValid && Object.keys(methods.formState.errors).length === 0;

  // TODO: determine if should live in this component or a helper
  const fields: { [fieldName: string]: TFormField } = {
    productId: {
      gridWidth: { xs: 12, sm: 12, md: 12 },
      name: 'productId',
      label: 'Product ',
      fieldType: FormFieldType.SelectField,
      required: true,
      fieldConfig: {
        selectField: {
          options: (filteredProductOfferings ?? [])?.map((option) => ({
            label: option?.name ?? '',
            value: option?.id ?? '',
          })),
        },
      },
    },
    businessClass: {
      gridWidth: { xs: 12, sm: 12, md: 12 },
      name: 'businessClass',
      label: 'Business Class',
      fieldType: FormFieldType.SelectField,
      required: true,
      fieldConfig: {
        selectField: {
          options: (businessClasses ?? [])?.map((option) => ({
            label: option.name,
            value: option.id,
          })),
        },
      },
    },
    policyEffectiveDate: {
      gridWidth: { xs: 12, sm: 6 },
      name: 'policyEffectiveDate',
      label: 'Policy Effective Date',
      fieldType: FormFieldType.DatePickerField,
      required: true,
      sx: { marginBottom: '24px' },
      fieldConfig: {
        dateField: {
          minDate: fakeLocalMidnightFromTZMidnight(minDate),
          maxDate: maxDate ? fakeLocalMidnightFromTZMidnight(maxDate) : undefined,
        },
      },
    },
    policyExpirationDate: {
      gridWidth: { xs: 12, sm: 6 },
      name: 'policyExpirationDate',
      label: 'Policy Expiration Date',
      fieldType: FormFieldType.DatePickerField,
      required: true,
      sx: { marginBottom: '24px' },
      fieldConfig: {
        dateField: {
          minDate: minDate ? addDays(formValues.policyEffectiveDate, 1) : undefined,
        },
      },
    },
  };

  return (
    <Dialog
      open={isOpen}
      size='small'
      title={`Continue to Quote for Indication #${indication?.referenceId}`}
      showBorders={false}
      handleCloseDialog={() => handleClose(true)}
      secondaryBtnText='Exit'
      secondaryBtnAction={() => handleClose(true)}
      primaryBtnText='Continue to Quote'
      primaryBtnAction={handleIndicationConversion}
      isPrimaryBtnDisabled={!isFormValid}
      isPrimaryBtnLoading={isPending}
    >
      {isLoading ? (
        <Loading />
      ) : (
        <Grid sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center', pb: 3, px: 4 }}>
          <Form methods={methods} formId='sign-up-form'>
            <FormSection fields={fields} styleConfig={{ spacing: 2 }} />
          </Form>
        </Grid>
      )}
    </Dialog>
  );
};
